import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  EnrollSourceType,
  ExperienceAnchorType,
  IExperienceAnchorEmployeeDate,
  IExperienceAnchorFixed,
  IExperienceTemplate,
  ISupportingActor
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField, IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import { Button, Dialog, Typography } from '@material-ui/core';
import { cloneDeep, compact, keyBy, sortBy } from 'lodash';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../../components/DialogContentWithoutPadding';
import { Loader } from '../../../../../components/Loader';
import { Dictionary } from '../../../../../hooks/useDictionary';
import { useSnackbar } from '../../../../../hooks/useSnackbar';
import { FlexContainer } from '../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  createExperienceInstance,
  enrollUsers,
  getAllActorsForSelectableParticipants,
  SelectableParticipantWithActors,
  useSelectableParticipantsWithTheirSupportingActors
} from '../../../../../services/experiences/template';
import { getAnchorOffsetForExperience } from '../../../../../services/experiences/timing';
import { useProductAnalytics } from '../../../../../services/productAnalytics';
import {
  addToTs,
  fromMoment,
  now,
  toMoment
} from '../../../../../services/time';
import { getUserValue } from '../../../../../services/user';
import { updateDataInDoc } from '../../../../../services/firebase';
import { AnchorField, useAnchorFields } from '../../../components/Anchors';
import { getInstancesDataForTracking } from './aggregate';
import { getFieldOverridesFromChanges } from '../modifications';
import {
  ListItem,
  PreviewAndConfigureDialogContent
} from './PreviewAndConfigureDialogContent';
import { SelectParticipantDialogContent } from './SelectParticipantDialogContent';

export const Padding = styled('div')((p) => ({
  padding: p.theme.spacing(4)
}));

const sortSupportingActors = (supportingActors: {
  [key: string]: ISupportingActor;
}): Dictionary<ISupportingActor> => {
  return keyBy(
    // Complete assumption that a manager or mentor key exists
    [
      ...sortBy(Object.values(supportingActors), (a) =>
        a.key === 'manager' || a.key === 'mentor' ? '0' : a.name
      )
    ],
    'key'
  );
};

// original instances

// everything that can be selected
// once something is selected, hold reference to the original item
// have a model in place to manipulate them

const DialogContentWithActionsSelectStep = ({
  tpl,
  customFields,
  anchorFields,
  selectableParticipants,
  selected,
  setSelected,
  onClose,
  goToNextStep
}: {
  tpl: Doc<IExperienceTemplate>;
  customFields: CustomUserField[];
  anchorFields: AnchorField[];
  selectableParticipants: SelectableParticipantWithActors[];
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  onClose: () => void;
  goToNextStep: () => void;
}) => {
  const { t } = useTranslation();
  const n = useMemo(() => {
    return now();
  }, []);
  return (
    <>
      <DialogContentWithoutPadding>
        <Padding>
          <SelectParticipantDialogContent
            selected={selected}
            setSelected={setSelected}
            selectableParticipants={selectableParticipants}
            actors={sortSupportingActors(tpl.data.supportingActors)}
            templateAnchor={tpl.data.anchor}
            anchorFields={anchorFields}
            orgId={tpl.data.orgId}
            tz={tpl.data.tz}
            customFields={customFields}
            templateRules={tpl.data.enrollRule}
            experienceTemplateId={tpl.id}
            n={n}
          />
        </Padding>
      </DialogContentWithoutPadding>
      <DialogActionsWithSlots
        right={
          <FlexContainer>
            <Button color="primary" onClick={() => onClose()}>
              {t('Cancel')}
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!selected.size}
              onClick={() => goToNextStep()}
            >
              {selected.size
                ? t('Preview and Configure ({{ count }})', {
                    count: selected.size
                  })
                : t('Preview and Configure')}
            </Button>
          </FlexContainer>
        }
      />
    </>
  );
};

const DialogContentWithActionsConfigureStep = ({
  tpl,
  anchorFields,
  selected,
  setSelected,
  goToPreviousStep,
  onClose,
  selectableParticipants,
  org
}: {
  tpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  goToPreviousStep: () => void;
  onClose: () => void;
  selectableParticipants: SelectableParticipantWithActors[];
  org: IOrganization;
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics();
  const { enqueueSnackbarSuccess } = useSnackbar();
  const currentUser = useCurrentUser();
  const { id: currentUserId } = useCurrentUser();
  const [showTableScreen, setShowTableScreen] = useState<boolean>(true);
  const [res, loading] = useSelectableParticipantsWithTheirSupportingActors(
    tpl
  );

  const originalInstances = useMemo(() => {
    const selectableByParticipantId = keyBy(
      selectableParticipants,
      (s) => s.participant.id
    );
    const us = compact(
      [...selected].map((userId) => selectableByParticipantId[userId])
    );

    const anchorOffset = getAnchorOffsetForExperience(tpl.data.anchor);
    // Use template's start time from anchor offset if available, otherwise fall back to org default
    const { hour: startHour, minute: startMinute } =
      tpl.data.anchor.offset.type === 'ABSOLUTE'
        ? {
            hour: tpl.data.anchor.offset.hour,
            minute: tpl.data.anchor.offset.minute ?? 0
          }
        : { hour: org.defaultExperienceStartHour, minute: 0 };

    let userInstances: { actors: ReturnType<typeof getAllActorsForSelectableParticipants>[number]; startsAt: ReturnType<typeof fromMoment> }[];

    // For FIXED anchor type: use the template's fixed date
    if (tpl.data.anchor.type === ExperienceAnchorType.FIXED) {
      const fixedAnchorDate = (tpl.data.anchor as IExperienceAnchorFixed).ts;
      // Apply the template's start time to the anchor date
      const anchorWithTime = fromMoment(
        toMoment(fixedAnchorDate)
          .tz(tpl.data.tz)
          .hour(startHour)
          .minute(startMinute)
          .second(0)
      );
      // Calculate startsAt from anchor date minus offset
      const startsAt = addToTs(anchorWithTime, -anchorOffset);

      userInstances = getAllActorsForSelectableParticipants(us, tpl).map((actors) => ({
        actors,
        startsAt
      }));
    }
    // For EMPLOYEE_DATE anchor type: use user's anchor date from their profile
    else if (tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE) {
      userInstances = getAllActorsForSelectableParticipants(us, tpl).map((actors) => {
        const anchorDate = getUserValue(
          actors.participant.user.data,
          (tpl.data.anchor as IExperienceAnchorEmployeeDate).field,
          now()
        );

        // Apply the template's start time to the user's anchor date
        const anchorWithTime = fromMoment(
          toMoment(anchorDate)
            .tz(tpl.data.tz)
            .hour(startHour)
            .minute(startMinute)
            .second(0)
        );
        // Calculate startsAt from anchor date minus offset
        // This respects the user's actual anchor date, even if in the past
        const startsAt = addToTs(anchorWithTime, -anchorOffset);

        return {
          actors,
          startsAt
        };
      });
    }
    // For IMMEDIATE anchor type: use today's date with template's start time
    else {
      const immediateStartsAt = fromMoment(
        moment.tz(org.tz).hour(startHour).minute(startMinute).second(0)
      );

      userInstances = getAllActorsForSelectableParticipants(us, tpl).map((actors) => ({
        actors,
        startsAt: immediateStartsAt
      }));
    }

    return userInstances.map((a) => {
      return {
        participant: a.actors.participant,
        instance: createExperienceInstance(
          tpl,
          [a.actors.participant, ...a.actors.supportingActors],
          a.startsAt,
          now(),
          { type: EnrollSourceType.MANUAL, userId: currentUserId }
        ),
        actors: Object.assign(
          {},
          ...a.actors.supportingActors.map((x) => ({ [x.key]: x.user }))
        )
      };
    });
    // This has no dependencies by design.
    // This is calculated when the screen comes up, then it acts like a stable model
    // This related to the problems mentioned in this ticket: https://app.shortcut.com/emplexp/story/11798/skipped-things-unskip-in-the-manual-enroll-modal
  }, []);

  const [editableInstances, setEditableInstances] = useState<ListItem[]>(
    cloneDeep(originalInstances)
  );

  const trackData = useMemo(() => {
    return getInstancesDataForTracking(editableInstances, originalInstances);
  }, [editableInstances, originalInstances]);

  return (
    <>
      <DialogContentWithoutPadding>
        <PreviewAndConfigureDialogContent
          tpl={tpl}
          anchorFields={anchorFields}
          originalInstances={originalInstances}
          editableInstances={editableInstances}
          setEditableInstances={setEditableInstances}
        />
      </DialogContentWithoutPadding>
      <DialogActionsWithSlots
        right={
          <FlexContainer>
            <Button
              color="primary"
              onClick={() => {
                const newSelected = new Set(
                  editableInstances.map((i) => i.participant.user.id)
                );
                setSelected(newSelected);
                goToPreviousStep();
              }}
            >
              {t('Back')}
            </Button>
            <ButtonWithPromise
              color="primary"
              variant="contained"
              disabled={editableInstances.length === 0}
              pending={`${t('Enrolling')}...`}
              onClick={() => {
                const experiences = editableInstances.map((item) => {
                  const originalItem = originalInstances.find(
                    (o) => o.participant.user.id === item.participant.user.id
                  );
                  if (!originalItem) {
                    return item.instance;
                  }
                  const fieldOverrides = getFieldOverridesFromChanges(
                    tpl,
                    originalItem.instance,
                    item.instance,
                    currentUser.id,
                    now()
                  );
                  return updateDataInDoc(item.instance, { fieldOverrides });
                });
                return enrollUsers(experiences, currentUser.id)
                  .then(() => {
                    onClose();
                    enqueueSnackbarSuccess(
                      t('enroll.successSnack', { count: experiences.length })
                    );
                    trackEvent({
                      eventType: PAEventType.EXPERIENCE_TPL,
                      name: 'Manual Enrollment Button Clicked',
                      'Experience Template ID': tpl.id,
                      Explanation:
                        'Admin manually enrolls users into an experience',
                      'Number Of Enrollees': experiences.length,
                      'Skipped Timeline Items': trackData?.skippedItems,
                      'Changed Supporting Actor': trackData.supportingActors,
                      'Changed Anchor': trackData.anchors
                    });
                  })
                  .catch((err) => {
                    console.log('ERR', err);
                    return Promise.reject(err);
                  });
              }}
            >
              {t('enrollParticipants', { count: editableInstances.length })}
            </ButtonWithPromise>
          </FlexContainer>
        }
      />
    </>
  );
};

const DialogContentWithActions = ({
  anchorFields,
  selected,
  selectableParticipants,
  setSelected,
  onClose,
  tpl,
  org
}: {
  anchorFields: AnchorField[];
  onClose: () => void;
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectableParticipants: SelectableParticipantWithActors[];
  tpl: Doc<IExperienceTemplate>;
  org: IOrganization;
}) => {
  const [step, setStep] = useState<'SELECT' | 'CONFIGURE'>('SELECT');

  if (step === 'SELECT') {
    return (
      <DialogContentWithActionsSelectStep
        tpl={tpl}
        customFields={org.customUserFields}
        anchorFields={anchorFields}
        selectableParticipants={selectableParticipants}
        selected={selected}
        setSelected={setSelected}
        onClose={onClose}
        goToNextStep={() => setStep('CONFIGURE')}
      />
    );
  } else {
    return (
      <DialogContentWithActionsConfigureStep
        tpl={tpl}
        org={org}
        anchorFields={anchorFields}
        selectableParticipants={selectableParticipants}
        selected={selected}
        setSelected={setSelected}
        onClose={onClose}
        goToPreviousStep={() => setStep('SELECT')}
      />
    );
  }
};

export const ManualEnrollModal = ({
  open,
  onClose,
  tpl
}: {
  open: boolean;
  onClose: () => void;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useTranslation();
  const { org } = useCurrentUser();
  const anchorFields = useAnchorFields(org.customUserFields);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [res, loading] = useSelectableParticipantsWithTheirSupportingActors(
    tpl
  );
  const selectableParticipants = useMemo(
    () => (res ? res.selectable : EMPTY_ARR),
    [res]
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogActionsHeader
          onClose={() => {
            onClose();
            setSelected(new Set());
          }}
          left={
            <Typography variant="h4">{t('Enroll Participants')}</Typography>
          }
        />
        {loading && <Loader height={500} />}
        {res && (
          <DialogContentWithActions
            anchorFields={anchorFields}
            tpl={tpl}
            selectableParticipants={selectableParticipants}
            selected={selected}
            onClose={onClose}
            setSelected={setSelected}
            org={org}
          />
        )}
      </Dialog>
    </>
  );
};
